/**
 * O que faz: passo "Exemplos" do assistente: três mensagens fictícias com o destino esperado, que a clínica
 *            pode trocar. A troca muda só os exemplos do rascunho; não treina IA nem faz chamada paga.
 * Onde usa: ConfiguracaoOrganizacaoKanban (passo 3).
 * Conexões: funções puras de rascunho.ts; Select compartilhado. Não importa Supabase nem fetch.
 * Tabelas: nenhuma.
 * Data: 21/10/2026.
 */
import { useId } from 'react';
import { MessageSquareQuote } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { alterarDestinoExemplo, destinoDoExemplo, EXEMPLOS_FICTICIOS, type ColunaRascunho } from './rascunho';

const MANTER = 'manter';

interface ExemplosOrganizacaoKanbanProps {
  colunas: ColunaRascunho[];
  onAlterar: (colunas: ColunaRascunho[]) => void;
}

export function ExemplosOrganizacaoKanban({ colunas, onAlterar }: ExemplosOrganizacaoKanbanProps) {
  const idBase = useId();
  const organizadas = colunas.filter((coluna) => coluna.organizar);
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Estas mensagens são <strong className="font-semibold text-foreground">exemplos fictícios</strong>, só para conferir o entendimento.
        Se algum destino estiver errado, troque. Isso ajusta apenas a sua configuração; não é teste de acerto da IA.
      </p>
      <ul className="space-y-3">
        {EXEMPLOS_FICTICIOS.map((exemplo, indice) => {
          const destino = destinoDoExemplo(colunas, exemplo.texto);
          const idCampo = `${idBase}-exemplo-${indice}`;
          return (
            <li key={exemplo.texto} className="space-y-2 rounded-xl border border-border p-3">
              <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <MessageSquareQuote className="h-3.5 w-3.5" aria-hidden="true" />Exemplo fictício {indice + 1}
              </p>
              <p className="text-sm text-foreground">“{exemplo.texto}”</p>
              <div className="space-y-1">
                <Label htmlFor={idCampo} className="text-xs">Para onde o card deve ir</Label>
                <Select value={destino ?? MANTER} onValueChange={(valor) => onAlterar(alterarDestinoExemplo(colunas, exemplo.texto, valor === MANTER ? null : valor))}>
                  <SelectTrigger id={idCampo} className="w-full sm:w-72"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={MANTER}>Manter onde está</SelectItem>
                    {organizadas.map((coluna) => <SelectItem key={coluna.etapaId} value={coluna.etapaId}>{coluna.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
